import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  fetchPublicEvents,
  fetchPublicMatches,
  registerMemberForEvent,
  type PublicEventItem,
  type PublicMatchItem
} from "@/features/public/api";
import { getEventDisplayTitle, getPublicEventDisplayType, type PublicEventDisplayType } from "@/features/events/presentation";
import { readStoredMemberUser, restoreMemberAccessToken, subscribeToMemberSessionChanges } from "@/features/public/memberSession";
import { formatEventScheduleLine } from "@/shared/events/dateRange";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

type AgendaCardItem = {
  id: string;
  source: "event" | "match";
  sourceId: string;
  title: string;
  type: PublicEventDisplayType;
  date: string;
  heure: string;
  dateFin: string | null;
  heureFin: string | null;
  lieu: string;
  status: "LIVE" | "UPCOMING" | "PAST";
  summary: string;
  accent: string;
  participants: number;
  maxParticipants: number;
};

type EventActionState = "register" | "login" | "closed" | "full" | null;

const typeConfig: Record<AgendaCardItem["type"], { color: string; bg: string; label: string; icon: string }> = {
  match: { color: "#1A1A1A", bg: "#11111110", label: "Match", icon: "ri-football-line" },
  entrainement: { color: "#1A1A1A", bg: "#11111110", label: "Entraînement", icon: "ri-run-line" },
  tournoi: { color: "#1A1A1A", bg: "#11111110", label: "Tournoi", icon: "ri-trophy-line" },
  general: { color: "#1A1A1A", bg: "#11111110", label: "Général", icon: "ri-calendar-event-line" }
};

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T12:00:00`);

  return {
    day: date.getDate().toString().padStart(2, "0"),
    month: date.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase(),
    weekday: date.toLocaleDateString("fr-FR", { weekday: "long" })
  };
}

function toTimestamp(date: string, heure: string) {
  return new Date(`${date}T${heure}:00`).getTime();
}

function mapPublicMatchToAgendaCard(match: PublicMatchItem): AgendaCardItem | null {
  if (match.statut === "CANCELLED" || match.statut === "COMPLETED") {
    return null;
  }

  const status =
    match.statut === "LIVE"
      ? "LIVE"
      : toTimestamp(match.date, match.heure) < Date.now()
        ? "PAST"
        : "UPCOMING";

  return {
    id: `match:${match.id}`,
    source: "match",
    sourceId: match.id,
    title: `${match.sport} - ${match.equipeDomicile} vs ${match.equipeExterieur}`,
    type: match.type === "TOURNAMENT" ? "tournoi" : "match",
    date: match.date,
    heure: match.heure,
    dateFin: null,
    heureFin: null,
    lieu: match.lieu,
    status,
    summary: status === "LIVE" ? "Rencontre en direct" : "Affiche validée",
    accent: status === "LIVE" ? "Suivi en cours" : `${match.equipeDomicile} / ${match.equipeExterieur}`,
    participants: 2,
    maxParticipants: 2
  };
}

function mapPublicEventToAgendaCard(event: PublicEventItem): AgendaCardItem | null {
  const type = getPublicEventDisplayType(event.type);

  if (!type || event.statut !== "PUBLISHED") {
    return null;
  }

  const status = toTimestamp(event.date, event.heure) < Date.now() ? "PAST" : "UPCOMING";

  return {
    id: `event:${event.id}`,
    source: "event",
    sourceId: event.id,
    title: getEventDisplayTitle(event.titre, type),
    type,
    date: event.date,
    heure: event.heure,
    dateFin: event.dateFin,
    heureFin: event.heureFin,
    lieu: event.lieu,
    status,
    summary: type === "entrainement" ? "Séance programmée" : "Événement publié",
    accent: `${event.participants}/${event.maxParticipants} participants`,
    participants: event.participants,
    maxParticipants: event.maxParticipants
  };
}

function sortAgendaCards(items: AgendaCardItem[]) {
  return [...items].sort((left, right) => {
    const leftWeight = left.status === "LIVE" ? 0 : left.status === "UPCOMING" ? 1 : 2;
    const rightWeight = right.status === "LIVE" ? 0 : right.status === "UPCOMING" ? 1 : 2;

    if (leftWeight !== rightWeight) {
      return leftWeight - rightWeight;
    }

    return toTimestamp(left.date, left.heure) - toTimestamp(right.date, right.heure);
  });
}

function toStatusPresentation(item: AgendaCardItem) {
  if (item.status === "LIVE") {
    return {
      summary: item.summary,
      accent: item.accent,
      progressColor: "#dc2626"
    };
  }

  if (item.status === "PAST") {
    return {
      summary: "Toujours visible",
      accent: item.accent,
      progressColor: "#f59e0b"
    };
  }

  return {
    summary: item.summary,
    accent: item.accent,
    progressColor: "#16a34a"
  };
}

function getEventActionState(item: AgendaCardItem, isLoggedIn: boolean): EventActionState {
  if (item.source !== "event") {
    return null;
  }

  if (item.participants >= item.maxParticipants) {
    return "full";
  }

  if (item.status === "PAST") {
    return "closed";
  }

  return isLoggedIn ? "register" : "login";
}

export default function EventsSection() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [agendaItems, setAgendaItems] = useState<AgendaCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberUser, setMemberUser] = useState(() => readStoredMemberUser());
  const [memberAccessToken, setMemberAccessToken] = useState<string | null>(null);
  const [registeringEventId, setRegisteringEventId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [matchesResult, eventsResult] = await Promise.allSettled([
      fetchPublicMatches(),
      fetchPublicEvents({ status: "PUBLISHED", page: 1, limit: 100 })
    ]);

    const matches = matchesResult.status === "fulfilled" ? matchesResult.value : [];
    const events = eventsResult.status === "fulfilled" ? eventsResult.value.items : [];

    const nextAgendaItems = sortAgendaCards([
      ...matches.map(mapPublicMatchToAgendaCard).filter((item): item is AgendaCardItem => Boolean(item)),
      ...events.map(mapPublicEventToAgendaCard).filter((item): item is AgendaCardItem => Boolean(item))
    ]).slice(0, 6);

    setAgendaItems(nextAgendaItems);

    if (matchesResult.status === "rejected" && eventsResult.status === "rejected") {
      setError("Impossible de charger les rencontres");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const syncMemberSession = async () => {
      const nextUser = readStoredMemberUser();
      const nextToken = await restoreMemberAccessToken().catch(() => null);

      if (!isMounted) {
        return;
      }

      setMemberUser(nextUser);
      setMemberAccessToken(nextToken);
    };

    void syncMemberSession();
    const unsubscribe = subscribeToMemberSessionChanges(() => {
      void syncMemberSession();
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    void loadMatches();

    const handleWindowFocus = () => {
      void loadMatches();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadMatches();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadMatches]);

  useRealtimeInvalidation({
    topics: ["matches", "events"],
    onInvalidate: () => {
      void loadMatches();
    }
  });

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timeout = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const handleRegisterForEvent = useCallback(
    async (item: AgendaCardItem) => {
      if (item.source !== "event") {
        return;
      }

      if (!memberAccessToken || !memberUser) {
        navigate("/formulaires?tab=login&redirect=%2Fsport%2Fevenements");
        return;
      }

      setRegisteringEventId(item.sourceId);

      try {
        await registerMemberForEvent(memberAccessToken, item.sourceId);
        await loadMatches();
        setToast({
          type: "success",
          message: `Votre inscription à ${item.title} a bien été enregistrée.`
        });
      } catch (caughtError) {
        const message = caughtError instanceof Error ? caughtError.message : "Inscription impossible pour le moment.";
        setToast({
          type: "error",
          message
        });
      } finally {
        setRegisteringEventId(null);
      }
    },
    [loadMatches, memberAccessToken, memberUser, navigate]
  );

  return (
    <section ref={ref} className="py-24 bg-white">
      {toast ? (
        <div className={`fixed top-24 right-6 z-50 rounded-2xl px-5 py-3 text-sm font-medium text-white shadow-xl ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          {toast.message}
        </div>
      ) : null}

      <div className="max-w-[1400px] mx-auto px-6">
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 text-[#E8521A] text-sm font-medium mb-3">
              Agenda
            </span>
            <h2 className="text-4xl font-bold text-[#1A1A1A]">Prochains Événements</h2>
          </div>
          <Link
            to="/sport/calendrier"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#E8521A] hover:gap-3 transition-all whitespace-nowrap"
          >
            Voir le calendrier complet
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-[326px] rounded-3xl border-2 border-gray-200 bg-white animate-pulse"></div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-10 text-center text-sm text-red-600">
            {error}
          </div>
        ) : agendaItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {agendaItems.map((item, index) => {
              const date = formatDate(item.date);
              const cfg = typeConfig[item.type];
              const status = toStatusPresentation(item);
              const actionState = getEventActionState(item, Boolean(memberUser));
              const isRegistering = registeringEventId === item.sourceId;
              const progressPct = item.maxParticipants > 0 ? Math.round((item.participants / item.maxParticipants) * 100) : 0;
              const progressColor = progressPct >= 100 ? "#dc2626" : progressPct >= 60 ? "#f59e0b" : "#16a34a";

              return (
                <div
                  key={item.id}
                  className={`bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-center">
                        <div className="text-4xl font-extrabold leading-none text-gray-900">{date.day}</div>
                        <div className="text-sm font-semibold text-gray-700 mt-1">{date.month}</div>
                      </div>
                      <span
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}
                      >
                        <i className={cfg.icon}></i>
                        {cfg.label}
                      </span>
                    </div>

                    <h3 className="font-bold text-[#1A1A1A] text-base mb-3 leading-snug">{item.title}</h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <i className="ri-calendar-line text-black"></i>
                        <span>{formatEventScheduleLine(item)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <i className="ri-map-pin-line text-black"></i>
                        <span className="truncate">{item.lieu}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1.5 gap-3">
                        <span className="truncate">{status.summary}</span>
                        <span className="whitespace-nowrap">{status.accent}</span>
                      </div>
                      <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.max(0, Math.min(progressPct, 100))}%`,
                            backgroundColor: progressColor
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-group-line text-black"></i>
                      <span>{item.accent}</span>
                    </div>

                    {actionState ? (
                      <div className="mt-4 flex justify-end">
                        {actionState === "register" ? (
                          <button
                            type="button"
                            onClick={() => void handleRegisterForEvent(item)}
                            disabled={isRegistering}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A8C7A] transition-colors hover:text-[#14685b] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <i className={`${isRegistering ? "ri-loader-4-line animate-spin" : "ri-user-add-line"}`}></i>
                            {isRegistering ? "Inscription..." : "Participer"}
                            <i className="ri-arrow-right-line"></i>
                          </button>
                        ) : actionState === "login" ? (
                          <button
                            type="button"
                            onClick={() => navigate("/formulaires?tab=login&redirect=%2Fsport%2Fevenements")}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8521A] transition-colors hover:text-[#d44a16]"
                          >
                            Se connecter pour participer
                            <i className="ri-arrow-right-line"></i>
                          </button>
                        ) : (
                          <span className={`inline-flex items-center gap-2 text-sm font-semibold ${actionState === "full" ? "text-amber-600" : "text-gray-400"}`}>
                            <i className={actionState === "full" ? "ri-alert-line" : "ri-time-line"}></i>
                            {actionState === "full" ? "Complet" : "Inscriptions clôturées"}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-white px-6 py-14 text-center">
            <i className="ri-calendar-event-line text-4xl text-gray-300"></i>
            <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucune rencontre programmée pour le moment</h3>
            <p className="mt-2 text-sm text-gray-500">
              Dès que l&apos;admin planifie une rencontre ou un événement sportif, il apparaîtra ici.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
